import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {USER_DATA} from 'user/user-prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import IconCheck from '@spothero/icons/check';
import HorizontalRule from '@spothero/ui/horizontal-rule';
import TextButton from '@spothero/ui/text-button';
import {userUpdate, userLogout} from 'user/user-actions';
import UserUtils from 'user/user-utils';
import {userChange} from 'checkout/checkout-actions';
import {initialState as checkoutInitialState} from 'checkout/checkout-reducer';
import CheckoutPanel from 'checkout/common/CheckoutPanel';
import CheckoutUserAuthentication from './user/CheckoutUserAuthentication';
import UserDetail from './user/UserDetail';

const HeroControls = loadable(() => import('./hero/HeroControls'));
const isHeroPurchasing = ({
    purchaseForCustomer,
    purchaseOnBehalfOfCustomer,
    isRebookingReservation,
} = {}) =>
    purchaseForCustomer || purchaseOnBehalfOfCustomer || isRebookingReservation;

export class AccountInformation extends Component {
    static propTypes = {
        currencyType: PropTypes.string,
        adminUser: PropTypes.shape(USER_DATA).isRequired,
        user: PropTypes.shape(USER_DATA).isRequired,
        isMonthly: PropTypes.bool,
        rebookReservationId: PropTypes.number,
        isRebookingReservation: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        purchaseOnBehalfOfCustomer: PropTypes.bool,
        spotId: PropTypes.number,
        selectedRate: PropTypes.object,
        highlight: PropTypes.bool,
        referralSource: PropTypes.string,
        onUserChange: PropTypes.func.isRequired,
        onUserUpdate: PropTypes.func.isRequired,
        onUserLogout: PropTypes.func.isRequired,
    };
    state = {
        focusGuestLogin: this.props.user.status !== UserUtils.AUTH_STATE.USER,
        focusUserAuthentication: false,
        heroPurchasing: isHeroPurchasing(this.props),
        showSignIn: false,
    };

    _onUserChange = data => {
        const {
            isMonthly,
            user: {status},
            onUserChange,
            onUserUpdate,
            onUserLogout,
        } = this.props;
        const additionalState = {
            // user clicked Sign In after being logged in as guest, show the sign in page instead of guest
            showSignIn: status === UserUtils.AUTH_STATE.GUEST && !data.user,
        };

        onUserChange({
            ...data,
            isMonthly,
        });

        if (!isEmpty(data.user)) {
            additionalState.focusGuestLogin = false;
            additionalState.focusUserAuthentication = false;

            // replace user with customer or reset to admin
            onUserUpdate(data.user);
        } else {
            additionalState.focusGuestLogin =
                status === UserUtils.AUTH_STATE.USER;
            additionalState.focusUserAuthentication =
                status === UserUtils.AUTH_STATE.GUEST;
            additionalState.heroPurchasing = false;

            onUserLogout(status);
        }

        this.setState(additionalState);
    };

    _onEditClick = () => {
        const {onUserLogout} = this.props;

        onUserLogout(UserUtils.AUTH_STATE.GUEST);

        this.setState({
            showSignIn: false,
            focusGuestLogin: true,
            focusUserAuthentication: true,
            heroPurchasing: false,
        });
    };

    _onHeroPurchasing = heroControlsFormData => {
        const {adminUser, user} = this.props;
        const {customer, reservation} = heroControlsFormData;
        const heroPurchasing = isHeroPurchasing(reservation);

        this.setState({
            heroPurchasing,
        });

        if (customer && heroPurchasing) {
            this._onUserChange({
                user: customer,
                extraReservationItems: reservation,
            });
        } else {
            const newUser = !isEmpty(adminUser) ? adminUser : user;

            this._onUserChange({
                user: newUser,
                extraReservationItems: {
                    ...reservation,
                    adminUser: checkoutInitialState().adminUser,
                },
            });
        }
    };

    _renderPreview() {
        const {
            user: {status, email},
        } = this.props;
        const {heroPurchasing} = this.state;
        const displayEmail =
            status !== UserUtils.AUTH_STATE.LOGGED_OUT ? email : null;
        const isGuest = status === UserUtils.AUTH_STATE.GUEST;

        if (!displayEmail) {
            return null;
        }

        return (
            <span className="CheckoutPanel-preview-email">
                {heroPurchasing ? 'Purchasing' : 'Checking out'} as{' '}
                {isGuest ? 'guest' : ''}{' '}
                <strong className="lo_sensitive">{displayEmail}</strong>
                {isGuest && (
                    <TextButton onClick={this._onEditClick}>Edit</TextButton>
                )}
            </span>
        );
    }

    _renderUserDetail() {
        const {user} = this.props;

        return <UserDetail user={user} onUserChange={this._onUserChange} />;
    }

    submit() {
        const {heroPurchasing} = this.state;
        const heroControlsFormData = heroPurchasing
            ? this._heroControlsForm.submit()
            : true;

        return heroControlsFormData ? heroControlsFormData : false;
    }

    render() {
        const {
            adminUser,
            user,
            currencyType,
            rebookReservationId,
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
            spotId,
            selectedRate,
            highlight,
            referralSource,
        } = this.props;
        const {
            focusGuestLogin,
            focusUserAuthentication,
            heroPurchasing,
            showSignIn,
        } = this.state;
        const asAdmin = !isEmpty(adminUser) || user.isAdmin;
        const isOpen =
            (selectedRate && user.status === UserUtils.AUTH_STATE.LOGGED_OUT) ||
            asAdmin ||
            heroPurchasing;
        const classes = classNames(
            'CheckoutPanel-account',
            {'CheckoutPanel-completed': selectedRate && !isOpen},
            {highlight}
        );

        return (
            <CheckoutPanel
                className={classes}
                title={
                    <>
                        {isOpen ? (
                            <em>1</em>
                        ) : (
                            <em>
                                <IconCheck />
                            </em>
                        )}{' '}
                        Account Info
                    </>
                }
                additionalTitleMarkup={
                    selectedRate && (!isOpen || asAdmin)
                        ? this._renderUserDetail()
                        : null
                }
                open={isOpen}
                preview={this._renderPreview()}
            >
                {(asAdmin || heroPurchasing) && this._renderPreview()}
                <div className="AccountInformation">
                    {!heroPurchasing && !asAdmin && (
                        <CheckoutUserAuthentication
                            user={user}
                            referralSource={referralSource}
                            focusGuestLogin={focusGuestLogin}
                            focusUserAuthentication={focusUserAuthentication}
                            showSignIn={showSignIn}
                            onUserChange={this._onUserChange}
                        />
                    )}
                    {(asAdmin || heroPurchasing) && (
                        <>
                            <HorizontalRule lineStyle="solid" />
                            <HeroControls
                                ref={node => {
                                    this._heroControlsForm = node;
                                }}
                                adminUser={
                                    !isEmpty(adminUser) ? adminUser : user
                                }
                                customer={!isEmpty(adminUser) ? user : null}
                                currencyType={currencyType}
                                spotId={spotId}
                                rebookReservationId={rebookReservationId}
                                isRebookingReservation={isRebookingReservation}
                                purchaseForCustomer={purchaseForCustomer}
                                purchaseOnBehalfOfCustomer={
                                    purchaseOnBehalfOfCustomer
                                }
                                onHeroPurchasing={this._onHeroPurchasing}
                            />
                        </>
                    )}
                </div>
            </CheckoutPanel>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            adminUser,
            currencyType,
            highlightAccountPanel: highlight,
            referralSource,
            isRebookingReservation,
            purchaseForCustomer,
            purchaseOnBehalfOfCustomer,
        },
        searchRequest: {
            monthly: isMonthly,
            rebook_reservation_id: rebookReservationId,
        },
        spot: {
            selected: {spotId, selectedRate},
        },
        user: {data: user},
    } = state;

    return {
        adminUser,
        currencyType,
        user,
        isMonthly,
        rebookReservationId,
        isRebookingReservation,
        purchaseForCustomer,
        purchaseOnBehalfOfCustomer,
        spotId,
        selectedRate,
        highlight,
        referralSource,
    };
};

const mapDispatchToProps = {
    onUserUpdate: userUpdate,
    onUserLogout: userLogout,
    onUserChange: userChange,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(AccountInformation);
